import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Wallet, FileSignature, Receipt, Download, LogOut, Folder, FolderOpen, MessageCircle, Search, Eye, Archive, X, Check } from "lucide-react";
import { motion } from "framer-motion";
import { api, API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PortalMessages from "./PortalMessages";
import CompanySwitcher from "./CompanySwitcher";

const DEFAULT_ICONS = {
  nominas: Wallet,
  impuestos: FileText,
  contratos: FileSignature,
  facturas: Receipt,
};

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState({});
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [preview, setPreview] = useState(null); // { url, name, type } | null
  const [zipping, setZipping] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showMessages, setShowMessages] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/portal/documents").then((r) => {
      const data = r.data || { folders: [], documents: {} };
      setFolders(data.folders || []);
      setDocs(data.documents || {});
      setActive((prev) => (prev && (data.folders || []).some((f) => f.key === prev)) ? prev : (data.folders?.[0]?.key || null));
      setLoading(false);
    });
  }, [reloadKey]);

  const total = useMemo(
    () => Object.values(docs).reduce((n, arr) => n + (arr?.length || 0), 0),
    [docs]
  );

  const [q, setQ] = useState("");
  useEffect(() => { setSelected(new Set()); }, [active, q]);
  const allDocs = useMemo(
    () => folders.flatMap((f) => (docs[f.key] || []).map((d) => ({ ...d, _folderLabel: f.label }))),
    [folders, docs]
  );
  const query = q.trim().toLowerCase();
  const results = query ? allDocs.filter((d) => (d.original_name || "").toLowerCase().includes(query)) : null;
  const displayDocs = results !== null ? results : (active ? (docs[active] || []) : []);
  const newInFolder = (key) => (docs[key] || []).filter((d) => d.is_new).length;

  const activeMeta = folders.find((f) => f.key === active) || folders[0];
  const folderByKey = useMemo(() => Object.fromEntries(folders.map((f) => [f.key, f])), [folders]);
  const subfolders = folders.filter((f) => f.parent_key === active);
  const breadcrumb = useMemo(() => {
    const chain = [];
    let cur = folderByKey[active];
    let guard = 0;
    while (cur && guard < 20) { chain.unshift(cur); cur = cur.parent_key ? folderByKey[cur.parent_key] : null; guard += 1; }
    return chain;
  }, [active, folderByKey]);

  const download = async (d) => {
    try {
      const token = localStorage.getItem("anv_token");
      const res = await fetch(`${API}/portal/documents/${d.id}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_name;
      a.click();
      URL.revokeObjectURL(url);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Error al descargar el documento");
    }
  };

  const isPreviewable = (d) => {
    const ct = (d.content_type || "").toLowerCase();
    const n = (d.original_name || "").toLowerCase();
    return (
      ct.startsWith("image/") ||
      ct === "application/pdf" ||
      ct.startsWith("text/") ||
      /\.(pdf|png|jpe?g|gif|webp|txt|csv|log|md|json|xml)$/.test(n)
    );
  };

  const openPreview = async (d) => {
    try {
      const token = localStorage.getItem("anv_token");
      const res = await fetch(`${API}/portal/documents/${d.id}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreview({ url, name: d.original_name, type: blob.type || d.content_type || "" });
    } catch {
      toast.error("No se pudo abrir la vista previa");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setReloadKey((k) => k + 1);
  };

  const downloadFolderZip = async (folderKey, label) => {
    setZipping(true);
    try {
      const token = localStorage.getItem("anv_token");
      const res = await fetch(`${API}/portal/folders/${folderKey}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar la carpeta");
    } finally {
      setZipping(false);
    }
  };

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const markSeen = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      await api.post("/portal/documents/mark-seen", { doc_ids: ids });
      toast.success(`${ids.length} marcado(s) como leído(s)`);
      setSelected(new Set());
      setReloadKey((k) => k + 1);
    } catch {
      toast.error("No se pudo marcar como leído");
    }
  };

  const markAllSeen = async () => {
    const ids = allDocs.filter((d) => d.is_new).map((d) => d.id);
    if (!ids.length) return toast.info("No hay documentos sin leer");
    try {
      await api.post("/portal/documents/mark-seen", { doc_ids: ids });
      toast.success("Todo marcado como leído");
      setSelected(new Set());
      setReloadKey((k) => k + 1);
    } catch {
      toast.error("No se pudo marcar como leído");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-200">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md border border-[#D4AF37]/60 grid place-items-center font-serif text-[#D4AF37]">
              A
            </div>
            <div className="text-sm text-zinc-300">Portal privado · <span className="text-white font-medium">{user?.name}</span></div>
          </div>
          <div className="flex items-center gap-3">
            <CompanySwitcher refreshKey={reloadKey} onSwitch={() => setReloadKey((k) => k + 1)} />
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="portal-logout"
              className="text-sm px-4 py-2 rounded-full border border-white/10 hover:border-white/30 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid md:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="space-y-2">
          <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-4">Carpetas</div>
          {folders.filter((m) => !m.parent_key).map((meta) => {
            const Icon = DEFAULT_ICONS[meta.key] || FolderOpen;
            const subs = folders.filter((s) => s.parent_key === meta.key);
            const count = (docs[meta.key]?.length || 0) + subs.reduce((n, s) => n + (docs[s.key]?.length || 0), 0);
            const newC = newInFolder(meta.key) + subs.reduce((n, s) => n + newInFolder(s.key), 0);
            const activeHere = breadcrumb[0]?.key === meta.key;
            return (
              <button
                key={meta.key}
                onClick={() => setActive(meta.key)}
                data-testid={`portal-folder-${meta.key}`}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                  activeHere
                    ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-white"
                    : "border-white/5 text-zinc-400 hover:text-white hover:border-white/20"
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {meta.label}
                </span>
                <span className="flex items-center gap-2">
                  {newC > 0 && (
                    <span
                      data-testid={`sidebar-new-${meta.key}`}
                      className="text-[10px] font-semibold min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#D4AF37] text-black"
                    >
                      {newC}
                    </span>
                  )}
                  <span className="text-xs font-mono text-zinc-500">{count}</span>
                </span>
              </button>
            );
          })}
          <button
            onClick={() => setShowMessages(true)}
            data-testid="portal-open-messages"
            className="w-full mt-4 flex items-center gap-3 px-4 py-3 rounded-lg border border-white/5 text-zinc-300 hover:text-white hover:border-[#D4AF37]/40 transition-colors"
          >
            <MessageCircle className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            Escríbenos
          </button>
        </aside>

        {/* Main */}
        <section>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Área privada</div>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight">
              Hola, <em className="gold-text not-italic">{user?.name?.split(" ")[0]}</em>.
            </h1>
            <p className="text-zinc-400 mt-2">Tienes {total} documento{total !== 1 ? "s" : ""} disponibles.</p>
          </div>

          {/* Folder cards summary */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {folders.filter((m) => !m.parent_key).map((meta, i) => {
              const Icon = DEFAULT_ICONS[meta.key] || FolderOpen;
              const subs = folders.filter((s) => s.parent_key === meta.key);
              const directCount = docs[meta.key]?.length || 0;
              const subCount = subs.reduce((n, s) => n + (docs[s.key]?.length || 0), 0);
              const count = directCount + subCount;
              const newCount = newInFolder(meta.key) + subs.reduce((n, s) => n + newInFolder(s.key), 0);
              return (
                <motion.button
                  key={meta.key}
                  onClick={() => setActive(meta.key)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`text-left p-5 rounded-xl border transition-colors ${
                    active === meta.key ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-white/5 bg-[#121214] hover:border-white/20"
                  }`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                    {newCount > 0 && (
                      <span data-testid={`card-new-${meta.key}`} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4AF37] text-black font-semibold">
                        {newCount} nuevo{newCount !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div className="font-serif text-2xl text-white">{meta.label}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {count} archivo{count !== 1 ? "s" : ""}
                    {subs.length ? ` · ${subs.length} subcarpeta${subs.length !== 1 ? "s" : ""}` : ""}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Search + mark all as read */}
          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar documento por nombre en todas las carpetas…"
                data-testid="portal-doc-search"
                className="w-full bg-[#121214] border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            {allDocs.some((d) => d.is_new) && (
              <button
                onClick={markAllSeen}
                data-testid="mark-all-read"
                className="flex-shrink-0 px-5 py-3 rounded-xl border border-[#D4AF37]/40 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Marcar todo como leído
              </button>
            )}
          </div>

          {/* Active folder documents */}
          <div className="bg-[#121214] border border-white/5 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Folder className="w-4 h-4 text-[#D4AF37]" />
                <div className="font-serif text-xl text-white">
                  {results !== null ? `Resultados: “${q}”` : (activeMeta?.label || "—")}
                </div>
              </div>
              <div className="text-xs text-zinc-500">{displayDocs.length} documento(s)</div>
            </div>
            {results === null && breadcrumb.length > 0 && (
              <div className="px-6 pt-4">
                <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap" data-testid="portal-breadcrumb">
                  {breadcrumb.map((b, i) => (
                    <span key={b.key} className="flex items-center gap-2">
                      {i > 0 && <span className="text-zinc-600">›</span>}
                      <button
                        onClick={() => setActive(b.key)}
                        data-testid={`crumb-${b.key}`}
                        className={i === breadcrumb.length - 1 ? "text-white" : "hover:text-[#D4AF37]"}
                      >
                        {b.label}
                      </button>
                    </span>
                  ))}
                </div>
                {subfolders.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="portal-subfolders">
                    {subfolders.map((sf) => {
                      const c = docs[sf.key]?.length || 0;
                      const n = newInFolder(sf.key);
                      return (
                        <button
                          key={sf.key}
                          onClick={() => setActive(sf.key)}
                          data-testid={`subfolder-tile-${sf.key}`}
                          className="text-left p-4 rounded-xl border border-white/10 bg-black/30 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between"
                        >
                          <span className="flex items-center gap-2 text-white truncate">
                            <FolderOpen className="w-4 h-4 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                            <span className="truncate">{sf.label}</span>
                          </span>
                          <span className="flex items-center gap-2 flex-shrink-0">
                            {n > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37] text-black font-semibold">{n}</span>}
                            <span className="text-xs text-zinc-500 font-mono">{c}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {selected.size > 0 && (
              <div className="px-6 py-3 border-b border-white/5 flex items-center justify-between bg-[#D4AF37]/5" data-testid="selection-bar">
                <span className="text-xs text-zinc-300">{selected.size} seleccionado{selected.size !== 1 ? "s" : ""}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelected(new Set())} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-white">Cancelar</button>
                  <button onClick={markSeen} data-testid="mark-seen-btn" className="text-xs px-4 py-1.5 rounded-full bg-[#D4AF37] text-black font-medium inline-flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Marcar como leído
                  </button>
                </div>
              </div>
            )}
            {results === null && active && (docs[active]?.length > 0) && (
              <div className="px-6 py-3 border-b border-white/5 flex justify-end">
                <button
                  onClick={() => downloadFolderZip(active, activeMeta?.label || "carpeta")}
                  disabled={zipping}
                  data-testid="download-folder-zip"
                  className="text-xs px-4 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Archive className="w-3.5 h-3.5" />
                  {zipping ? "Preparando ZIP…" : "Descargar carpeta (ZIP)"}
                </button>
              </div>
            )}

            {loading ? (
              <div className="p-10 text-center text-zinc-500">Cargando documentos…</div>
            ) : displayDocs.length ? (
              <div className="divide-y divide-white/5">
                {displayDocs.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors"
                    data-testid={`doc-row-${d.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggleSelect(d.id)}
                      data-testid={`select-doc-${d.id}`}
                      className="mr-4 w-4 h-4 accent-[#D4AF37] flex-shrink-0 cursor-pointer"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-white truncate flex items-center gap-2">
                        {d.is_new && (
                          <span
                            data-testid={`doc-new-badge-${d.id}`}
                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] flex-shrink-0"
                          >
                            Nuevo
                          </span>
                        )}
                        <span className="truncate">{d.original_name}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 font-mono">
                        {d._folderLabel ? `${d._folderLabel} · ` : ""}
                        {new Date(d.uploaded_at).toLocaleDateString("es-ES")} · {humanSize(d.size)}
                      </div>
                    </div>
                    <div className="ml-4 flex items-center gap-2 flex-shrink-0">
                      {isPreviewable(d) && (
                        <button
                          onClick={() => openPreview(d)}
                          data-testid={`preview-btn-${d.id}`}
                          className="px-4 py-2 rounded-full border border-white/10 text-zinc-300 text-sm hover:border-[#D4AF37]/50 hover:text-white transition-colors flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" /> Ver
                        </button>
                      )}
                      <button
                        onClick={() => download(d)}
                        data-testid={`download-btn-${d.id}`}
                        className="px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#FDE047] transition-colors flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" /> Descargar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-16 text-center">
                <Folder className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1} />
                <div className="text-zinc-500">
                  {results !== null ? "Sin resultados para tu búsqueda." : "Aún no hay documentos en esta carpeta."}
                </div>
                {results === null && (
                  <div className="text-xs text-zinc-600 mt-2">Tu asesor los subirá cuando estén listos.</div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {showMessages && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          data-testid="messages-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowMessages(false); }}
        >
          <div className="w-full max-w-2xl my-8 relative">
            <button
              onClick={() => setShowMessages(false)}
              data-testid="messages-modal-close"
              className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-[#121214] border border-white/10 grid place-items-center text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <PortalMessages />
          </div>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col p-4 md:p-8"
          data-testid="preview-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closePreview(); }}
        >
          <div className="w-full max-w-5xl mx-auto flex-1 flex flex-col bg-[#121214] border border-white/10 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="text-sm text-white truncate flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <span className="truncate">{preview.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => { const a = document.createElement("a"); a.href = preview.url; a.download = preview.name; a.click(); }}
                  data-testid="preview-download-btn"
                  className="px-3 py-1.5 rounded-full bg-[#D4AF37] text-black text-xs font-medium hover:bg-[#FDE047] transition-colors flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> Descargar
                </button>
                <button onClick={closePreview} data-testid="preview-close-btn" className="text-zinc-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-black/40 overflow-auto grid place-items-center">
              {preview.type.startsWith("image/") ? (
                <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" />
              ) : preview.type === "application/pdf" || /\.pdf$/i.test(preview.name) ? (
                <iframe src={preview.url} title={preview.name} className="w-full h-full min-h-[70vh]" />
              ) : preview.type.startsWith("text/") || /\.(txt|csv|log|md|json|xml)$/i.test(preview.name) ? (
                <iframe src={preview.url} title={preview.name} className="w-full h-full min-h-[70vh] bg-white" />
              ) : (
                <div className="text-center text-zinc-400 p-10">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-zinc-600" strokeWidth={1} />
                  No se puede previsualizar este tipo de archivo. Descárgalo para verlo.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
